import React from 'react'
import styled from '@emotion/styled'
import type { KambiEvent, KambiLiveData } from '@mobi/api-types/kambi'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { TeamIcon } from '@mobi/sports-offering/Components/Common/TeamsIcon/Components/TeamIcon'
import { getEventDetailsFromEvent } from '@mobi/sports-offering/helpers/getEventDetailsFromEvent'
import { getBaseballInningText } from './helpers/getBaseballText'
import { getCurrentQuarter } from './helpers/getCurrentQuarter'
import { MatchClock } from './hooks/useMatchClockDisplay'

export const ScoreCardDisplay: React.FC<{
  event: KambiEvent
  clock?: KambiLiveData['matchClock']
  score?: KambiLiveData['score']
  shouldHideClock?: boolean
  shouldUseDarkTheme?: boolean
  hasLiveEventFinished?: boolean
}> = ({ clock, score, event, shouldHideClock, shouldUseDarkTheme, hasLiveEventFinished }) => {
  const { leftTeam, rightTeam, shouldSwitchHomeAway } = getEventDetailsFromEvent(event)

  const leftScore = shouldSwitchHomeAway ? score?.away : score?.home
  const rightScore = shouldSwitchHomeAway ? score?.home : score?.away

  return (
    <WrapperStyled data-dark-theme={shouldUseDarkTheme ? 'true' : undefined}>
      <div className='score-card-display__row'>
        <div className='score-card-display__team score-card-display__team--left'>
          <div className='score-card-display__team-icon'>
            <TeamIcon
              sportName={event.sport}
              size='sm'
              participantId={leftTeam.participantId}
              defaultIconType='team-icon'
              eventId={event.id}
            />
          </div>

          <div className='score-card-display__team-name'>
            <span>{leftTeam.name}</span>
          </div>
        </div>

        <div className='score-card-display__middle'>
          <div className='score-card-display__score'>
            {score && (
              <span>
                {event.sport === 'GOLF'
                  ? `(${leftScore}) - (${rightScore})`
                  : `${leftScore} - ${rightScore}`}
              </span>
            )}
          </div>

          <div className='score-card-display__clock'>
            {shouldHideClock ? (
              <span>-</span>
            ) : (
              score && <span>{getClockDisplay(event, score, clock, hasLiveEventFinished)}</span>
            )}
          </div>
        </div>

        <div className='score-card-display__team score-card-display__team--right'>
          <div className='score-card-display__team-name'>
            <span>{rightTeam.name}</span>
          </div>

          <div className='score-card-display__team-icon'>
            <TeamIcon
              sportName={event.sport}
              size='sm'
              participantId={rightTeam.participantId}
              defaultIconType='team-icon'
              eventId={event.id}
            />
          </div>
        </div>
      </div>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

const getClockDisplay = (
  event: KambiEvent,
  score: KambiLiveData['score'],
  clock?: KambiLiveData['matchClock'],
  isEventFinished?: boolean
) => {
  if (isEventFinished) return 'FT'
  if (clock?.periodId === 'PS') return 'Penalties'
  if (event.sport === 'BASEBALL') return getBaseballInningText(score)

  if (clock) {
    if (countdownSports.has(event.sport)) {
      return <MatchClock clock={clock} shouldShowPeriod isCountdown />
    }

    return <MatchClock clock={clock} shouldShowPeriod={event.sport !== 'FOOTBALL'} />
  }

  if (score.info) return getCurrentQuarter(score)

  return null
}

const countdownSports = new Set([
  'E_BASKETBALL',
  'BASKETBALL',
  'AMERICAN_FOOTBALL',
  'ICE_HOCKEY',
  'AUSTRALIAN_RULES',
  'NETBALL',
  'HANDBALL',
  'WATER_POLO',
  'LACROSSE',
])

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  '--score-card-display-text-color': colors.neutral[800],
  '--score-card-display-score-color': colors.studio[900],

  '&[data-dark-theme="true"]': {
    '--score-card-display-text-color': colors.studio[200],
    '--score-card-display-score-color': colors.white,
  },

  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',

  '.score-card-display__row': {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 13rem minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: spacing.md,
    width: '100%',
  },

  '.score-card-display__team': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.smx1,
    minWidth: 0,
  },

  '.score-card-display__team--left': {
    justifyContent: 'flex-start',
  },

  '.score-card-display__team--right': {
    justifyContent: 'flex-end',
  },

  '.score-card-display__team-name': {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,

    '> span': {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
      fontFamily: font.family.primary,
      color: 'var(--score-card-display-text-color)',
      fontSize: font.size.md.fontSize,
      fontWeight: font.weight.regular,
      lineHeight: font.size.md.lineHeight,
    },
  },

  '.score-card-display__team--left .score-card-display__team-name > span': {
    textAlign: 'left',
  },

  '.score-card-display__team--right .score-card-display__team-name > span': {
    textAlign: 'right',
  },

  '.score-card-display__middle': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '13rem',
    flexShrink: 0,
  },

  '.score-card-display__score': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '> span': {
      color: 'var(--score-card-display-score-color)',
      fontWeight: font.weight.semibold,
      fontFamily: font.family.primary,
      fontSize: font.size.xl2.fontSize,
      lineHeight: font.size.xl2.lineHeight,
      letterSpacing: font.size.xl2.letterSpacing,
      textAlign: 'center',
      margin: '0rem 1rem',
    },
  },

  '.score-card-display__clock': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--score-card-display-text-color)',
    textAlign: 'center',
    fontFamily: font.family.primary,
    fontSize: font.size.lg.fontSize,
    fontWeight: font.weight.regular,
    lineHeight: font.size.md.lineHeight,
  },
})
